import {
    ComponentProps,
    forwardRef,
    useEffect,
    useRef,
    type MutableRefObject,
    type Ref,
} from "react";

function mergeRefs<T>(...refs: Array<Ref<T> | undefined>) {
    return (value: T) => {
        refs.forEach((ref) => {
            if (typeof ref === "function") {
                ref(value);
            } else if (ref) {
                (ref as MutableRefObject<T | null>).current = value;
            }
        });
    };
}

export const textAreaFocusStyles = [
    //
    "transition-shadow ease-out duration-300",
    // border
    "outline outline-transparent",
    "focus:outline-blue-500",
    // shadow
    "ring-8 ring-transparent",
    "focus:ring focus:ring-blue-200",
].join(" ");

export const textAreaBlockStyles = [
    "block text-13 w-full rounded-md bg-[var(--diagram-control-bg)]",
    "text-[var(--slate12)]",
    "border border-[var(--diagram-control-border)]",
].join(" ");

export const textAreaDisabledStyles = [
    //
    "disabled:text-[var(--slate10)]",
].join(" ");

interface Props extends ComponentProps<"textarea"> {
    onValueChange?: (value: string) => void;
    forceAutoFocus?: boolean;
    placeholder?: string;
    extraStyle?: string;
    paddingStyle?: string;
}

export type { Props as TextAreaProps };

export const TextArea = forwardRef<HTMLTextAreaElement, Props>(
    function TextArea(props, forwardedRef) {
        const {
            onValueChange,
            forceAutoFocus,
            placeholder,
            extraStyle,
            paddingStyle,
            ...native
        } = props;

        const innerRef = useRef<HTMLTextAreaElement>(null);
        const ref = mergeRefs(innerRef, forwardedRef);

        useEffect(() => {
            if (!forceAutoFocus) return;
            window.setTimeout(() => {
                innerRef.current?.focus();
            }, 100);
        }, [forceAutoFocus]);

        return (
            <textarea
                className={[
                    //
                    textAreaFocusStyles,
                    textAreaBlockStyles,
                    paddingStyle ? paddingStyle : "py-1.5 px-2",
                    textAreaDisabledStyles,
                    extraStyle ? extraStyle : "",
                ].join(" ")}
                onChange={(event) => {
                    onValueChange?.(event.currentTarget.value);
                }}
                onClick={(e) => {
                    e.currentTarget.focus();
                    e.stopPropagation();
                }}
                ref={ref}
                placeholder={placeholder}
                {...native}
            />
        );
    }
);
